import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, CircularProgress } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import client from '../api/client';
import CustomTextField from './CustomTextField';
import { toPersianDigits, toEnglishDigits } from '../utils/numberConverter';

const GRADIENT = 'linear-gradient(45deg, #1a237e 30%, #3949ab 90%)';
const GRADIENT_HOVER = 'linear-gradient(45deg, #0d1642 30%, #1a237e 90%)';

const emptyForm = { full_name: '', shop_name: '', city: '', mobile: '' };

/**
 * فرم مشترک ثبت‌نام و مشاوره.
 * onCancel: اگر داده شود (حالت پاپ‌اپ) دکمهٔ «انصراف» و در حالت موفقیت دکمهٔ «بستن» نمایش داده می‌شود.
 * onSuccess: کال‌بک اختیاری بعد از ثبت موفق.
 */
const RegisterConsultForm = ({ onCancel, onSuccess }) => {
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [captcha, setCaptcha] = useState({ a: 0, b: 0 });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const newCaptcha = () => {
    setCaptcha({
      a: Math.floor(Math.random() * 9) + 1,
      b: Math.floor(Math.random() * 9) + 1,
    });
    setCaptchaAnswer('');
  };

  useEffect(() => {
    newCaptcha();
  }, []);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!form.full_name.trim() || !form.shop_name.trim() || !form.city.trim() || !form.mobile.trim()) {
      toast.error('لطفاً همهٔ فیلدها را تکمیل کنید');
      return;
    }

    const answer = parseInt(toEnglishDigits(captchaAnswer), 10);
    if (answer !== captcha.a + captcha.b) {
      toast.error('پاسخ تست امنیتی نادرست است');
      newCaptcha();
      return;
    }

    setLoading(true);
    try {
      await client.post('/users/consultation-request/', {
        full_name: form.full_name.trim(),
        shop_name: form.shop_name.trim(),
        city: form.city.trim(),
        mobile: toEnglishDigits(form.mobile).trim(),
      });
      setSuccess(true);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('خطا در ثبت درخواست. دوباره تلاش کنید');
      newCaptcha();
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
          درخواست شما ثبت شد
        </Typography>
        <Typography color="text.secondary" sx={{ lineHeight: 2 }}>
          تا ۶ ساعت آینده همکاران ما با شما تماس می‌گیرند.
        </Typography>
        <Button
          variant="contained"
          onClick={onCancel ? onCancel : () => navigate('/')}
          sx={{ mt: 3, background: GRADIENT, '&:hover': { background: GRADIENT_HOVER } }}
        >
          {onCancel ? 'بستن' : 'بازگشت به صفحهٔ اصلی'}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 2 }}>
        استفاده از برنامه رایگان می‌باشد. لطفاً جهت ثبت‌نام و مشاورهٔ اولیه فرم زیر را تکمیل فرمایید.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <CustomTextField label="نام و نام خانوادگی" value={form.full_name} onChange={handleChange('full_name')} fullWidth />
        <CustomTextField label="نام فروشگاه" value={form.shop_name} onChange={handleChange('shop_name')} fullWidth />
        <CustomTextField label="شهر" value={form.city} onChange={handleChange('city')} fullWidth />
        <CustomTextField label="شماره موبایل" type="tel" value={form.mobile} onChange={handleChange('mobile')} fullWidth />

        {/* تست امنیتی عددی */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 1,
            bgcolor: '#eef0f7', borderRadius: 2, minWidth: 110, justifyContent: 'center',
          }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: 18, fontFamily: 'monospace', color: '#1a237e' }}>
              {toPersianDigits(captcha.a)} + {toPersianDigits(captcha.b)} = ؟
            </Typography>
            <IconButton size="small" onClick={newCaptcha}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1 }}>
            <CustomTextField label="پاسخ" type="tel" value={captchaAnswer}
              onChange={(e) => setCaptchaAnswer(e.target.value)} fullWidth />
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={loading}>انصراف</Button>
        )}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ background: GRADIENT, minWidth: 140, '&:hover': { background: GRADIENT_HOVER } }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'ارسال درخواست'}
        </Button>
      </Box>
    </Box>
  );
};

export default RegisterConsultForm;
